import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AppService from "../services/AppService";
import AIService from "../services/AIService";
import SplashService from "../services/SplashService";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import type { App } from "../types/app";
import type {
  CreateSplashDTO,
  GeneratedSplashScreen,
  GeneratedSplashVariant,
  SplashCropSettings,
} from "../types/splash";
import type { IconMood, IconStyle } from "../types/icon";

const STYLES: IconStyle[] = [
  "flat",
  "gradient",
  "3d",
  "minimalist",
  "illustrative",
  "geometric",
];
const MOODS: IconMood[] = [
  "professional",
  "playful",
  "elegant",
  "bold",
  "calm",
];
const IMAGE_MODELS = [
  { value: "gemini-3-pro-image-preview", label: "Gemini 3 Pro Image Preview" },
  {
    value: "gemini-2.5-flash-image",
    label: "Gemini 2.5 Flash Image (Nano Banana)",
  },
  { value: "gpt-image-2.5-flare", label: "OpenAI GPT Image 2.5 Flare" },
  { value: "gpt-image-2.5-sunburst", label: "OpenAI GPT Image 2.5 Sunburst" },
];

export default function AppSplashScreenGenerator() {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [records, setRecords] = useState<GeneratedSplashScreen[]>([]);

  const [style, setStyle] = useState<IconStyle>("minimalist");
  const [mood, setMood] = useState<IconMood>("professional");
  const [styleMode, setStyleMode] = useState<"manual" | "ai">("ai");
  const [imageModel, setImageModel] = useState("gpt-image-2.5-flare");
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [keywordsInput, setKeywordsInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [avoidInput, setAvoidInput] = useState("");
  const [avoidElements, setAvoidElements] = useState<string[]>([]);
  const [customPrompt, setCustomPrompt] = useState("");
  const [aiStylePromptDraft, setAiStylePromptDraft] = useState("");
  const [aiSuggestedStyle, setAiSuggestedStyle] =
    useState<IconStyle>("minimalist");
  const [aiSuggestedMood, setAiSuggestedMood] =
    useState<IconMood>("professional");

  const [loading, setLoading] = useState(false);
  const [generatingAIStyle, setGeneratingAIStyle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useToastStateSync({ success, error });

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportTarget, setExportTarget] = useState<GeneratedSplashScreen | null>(
    null,
  );
  const [cropSettings, setCropSettings] = useState<SplashCropSettings>({
    offsetX: 0,
    offsetY: 0,
    zoom: 1,
  });
  const [variants, setVariants] = useState<GeneratedSplashVariant[]>([]);
  const [processingVariants, setProcessingVariants] = useState(false);

  const addChip = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
    list: string[],
    listSetter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    const normalized = value.trim();
    if (!normalized) return;
    if (!list.includes(normalized)) {
      listSetter((prev) => [...prev, normalized]);
    }
    setter("");
  };

  const removeChip = (
    value: string,
    listSetter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    listSetter((prev) => prev.filter((item) => item !== value));
  };

  const parseJson = <T,>(text: string): T | null => {
    const raw = String(text || "").trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  };

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    try {
      const userApps = await AppService.getUserApps(user.email);
      setApps(userApps);
      if (!selectedAppId && userApps.length > 0) {
        setSelectedAppId(userApps[0].appId);
        setSelectedApp(userApps[0]);
      }
    } catch (loadError) {
      console.error(loadError);
      setError("Failed to load registered apps.");
    }
  }, [selectedAppId, user?.email]);

  const loadRecords = useCallback(async () => {
    if (!selectedApp || !user?.email) return;
    try {
      const rows = await SplashService.getSplashScreensByAppId(
        selectedApp.appId,
        user.email,
      );
      setRecords(rows);
    } catch (loadError) {
      console.error(loadError);
      setError("Failed to load splash screen history.");
    }
  }, [selectedApp, user?.email]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  useEffect(() => {
    const nextSelected = apps.find((item) => item.appId === selectedAppId) || null;
    setSelectedApp(nextSelected);
  }, [apps, selectedAppId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleGenerateAIStyle = async () => {
    if (!selectedApp || !user?.email) {
      setError("Select an app and sign in first.");
      return;
    }

    try {
      setGeneratingAIStyle(true);
      setError(null);
      setSuccess(null);

      const stylePrompt = [
        "You are an expert mobile splash screen art director.",
        "Generate concise visual direction for app splash screen generation.",
        "Focus on one hero subject, high contrast, strong center composition, and clean premium look.",
        "No text in the splash image.",
        "",
        `App name: ${selectedApp.name}`,
        `Category: ${selectedApp.category || "general"}`,
        `Platform target: ${selectedApp.platform || "both"}`,
        `Description: ${selectedApp.description || "N/A"}`,
        `Research context: ${
          selectedApp.researchContext?.whatItDoes ||
          selectedApp.researchContext?.summary ||
          "N/A"
        }`,
        "",
        "Return JSON only with:",
        '{ "style": "flat|gradient|3d|minimalist|illustrative|geometric", "mood": "professional|playful|elegant|bold|calm", "stylePrompt": "detailed splash direction in 2-4 sentences" }',
      ].join("\n");

      const styleResult = await AIService.generateJSON<{
        style?: string;
        mood?: string;
        stylePrompt?: string;
      }>(stylePrompt, {
        provider: "openai",
        model: "gpt-4.1",
        structuredOutputName: "splash_style_brief",
        structuredOutputSchema: {
          type: "object",
          properties: {
            style: { type: "string" },
            mood: { type: "string" },
            stylePrompt: { type: "string" },
          },
          required: ["style", "mood", "stylePrompt"],
        },
        temperature: 0.6,
        maxTokens: 500,
      });

      const nextStyle = STYLES.includes(
        String(styleResult.style || "").toLowerCase() as IconStyle,
      )
        ? (String(styleResult.style || "").toLowerCase() as IconStyle)
        : "minimalist";
      const nextMood = MOODS.includes(
        String(styleResult.mood || "").toLowerCase() as IconMood,
      )
        ? (String(styleResult.mood || "").toLowerCase() as IconMood)
        : "professional";
      const nextPrompt = String(styleResult.stylePrompt || "").trim();

      if (!nextPrompt) {
        throw new Error("Failed to generate a usable splash style direction.");
      }

      setAiSuggestedStyle(nextStyle);
      setAiSuggestedMood(nextMood);
      setStyle(nextStyle);
      setMood(nextMood);
      setAiStylePromptDraft(nextPrompt);
      setSuccess("AI splash style generated.");
    } catch (generateError: any) {
      console.error(generateError);
      const parsedFallback = parseJson<{
        style?: string;
        mood?: string;
        stylePrompt?: string;
      }>(String(generateError?.message || ""));
      if (parsedFallback?.stylePrompt) {
        setAiStylePromptDraft(parsedFallback.stylePrompt);
      }
      setError(generateError?.message || "Failed to generate AI style.");
    } finally {
      setGeneratingAIStyle(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedApp || !user?.email) {
      setError("Select an app and sign in first.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (styleMode === "ai" && !aiStylePromptDraft.trim()) {
        throw new Error(
          "Generate splash style first (or enter style prompt) before generating.",
        );
      }

      const dto: CreateSplashDTO = {
        appId: selectedApp.appId,
        appName: selectedApp.name,
        appDescription: selectedApp.description || "",
        appCategory: selectedApp.category,
        appPlatform: selectedApp.platform,
        appStoreUrl: selectedApp.storeUrl,
        androidStoreUrl: selectedApp.androidStoreUrl,
        iosStoreUrl: selectedApp.iosStoreUrl,
        researchContext: selectedApp.researchContext,
        style: styleMode === "ai" ? aiSuggestedStyle : style,
        mood: styleMode === "ai" ? aiSuggestedMood : mood,
        styleMode,
        aiStylePrompt:
          styleMode === "ai" ? aiStylePromptDraft.trim() : undefined,
        aiSuggestedStyle,
        aiSuggestedMood,
        colorScheme: {
          primary: primaryColor || undefined,
          secondary: secondaryColor || undefined,
          accent: accentColor || undefined,
        },
        keywords: styleMode === "ai" ? [] : keywords,
        avoidElements: styleMode === "ai" ? [] : avoidElements,
        customPrompt: styleMode === "ai" ? "" : customPrompt,
        model: imageModel || undefined,
      };

      const created = await SplashService.generateSplashScreenPair(user.email, dto);
      setRecords((prev) => [created, ...prev]);
      setSuccess("Generated light + dark splash screens.");
    } catch (generateError: any) {
      console.error(generateError);
      setError(generateError?.message || "Failed to generate splash screens.");
    } finally {
      setLoading(false);
    }
  };

  const openExportDialog = (record: GeneratedSplashScreen) => {
    setExportTarget(record);
    setVariants([]);
    setCropSettings({ offsetX: 0, offsetY: 0, zoom: 1 });
    setExportDialogOpen(true);
  };

  const handleGenerateVariants = async () => {
    if (!exportTarget) return;
    try {
      setProcessingVariants(true);
      setError(null);
      const generated = await SplashService.generateSplashVariants(
        exportTarget,
        cropSettings,
      );
      setVariants(generated);
      setSuccess("Capacitor splash assets prepared.");
    } catch (variantError: any) {
      console.error(variantError);
      setError(variantError?.message || "Failed to prepare splash assets.");
    } finally {
      setProcessingVariants(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!selectedApp || variants.length === 0) return;
    await SplashService.downloadVariants(variants, selectedApp.name);
  };

  const handleDelete = async (splash: GeneratedSplashScreen) => {
    if (!user?.email) return;
    try {
      await SplashService.deleteSplashScreen(splash.splashId, user.email);
      setRecords((prev) => prev.filter((item) => item.splashId !== splash.splashId));
      setSuccess("Splash screen record deleted.");
    } catch (deleteError: any) {
      console.error(deleteError);
      setError(deleteError?.message || "Failed to delete splash screen record.");
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Splash Screen Creator
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create light and dark Capacitor splash source images, then export
        `assets/splash.png` and `assets/splash-dark.png` in one ZIP.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Registered App</InputLabel>
              <Select
                value={selectedAppId}
                label="Registered App"
                onChange={(e) => setSelectedAppId(String(e.target.value || ""))}
              >
                {apps.map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name} ({app.platform})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Style Mode</InputLabel>
              <Select
                value={styleMode}
                label="Style Mode"
                onChange={(e) =>
                  setStyleMode(e.target.value as "manual" | "ai")
                }
              >
                <MenuItem value="ai">AI Style Mode</MenuItem>
                <MenuItem value="manual">Manual Style</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Image Model</InputLabel>
              <Select
                value={imageModel}
                label="Image Model"
                onChange={(e) => setImageModel(String(e.target.value || ""))}
              >
                {IMAGE_MODELS.map((model) => (
                  <MenuItem key={model.value} value={model.value}>
                    {model.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {styleMode === "ai" ? (
            <>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={handleGenerateAIStyle}
                    disabled={generatingAIStyle || !selectedApp}
                  >
                    {generatingAIStyle
                      ? "Generating Splash Style..."
                      : "Generate Splash Style"}
                  </Button>
                  <Chip label={`Style: ${aiSuggestedStyle}`} size="small" />
                  <Chip label={`Mood: ${aiSuggestedMood}`} size="small" />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="AI Style Direction"
                  value={aiStylePromptDraft}
                  onChange={(e) => setAiStylePromptDraft(e.target.value)}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Style</InputLabel>
                  <Select
                    value={style}
                    label="Style"
                    onChange={(e) => setStyle(e.target.value as IconStyle)}
                  >
                    {STYLES.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Mood</InputLabel>
                  <Select
                    value={mood}
                    label="Mood"
                    onChange={(e) => setMood(e.target.value as IconMood)}
                  >
                    {MOODS.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Primary Color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Secondary Color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Accent Color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Keyword"
                  value={keywordsInput}
                  onChange={(e) => setKeywordsInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addChip(keywordsInput, setKeywordsInput, keywords, setKeywords);
                    }
                  }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  {keywords.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      onDelete={() => removeChip(item, setKeywords)}
                    />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Avoid Element"
                  value={avoidInput}
                  onChange={(e) => setAvoidInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addChip(
                        avoidInput,
                        setAvoidInput,
                        avoidElements,
                        setAvoidElements,
                      );
                    }
                  }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  {avoidElements.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      onDelete={() => removeChip(item, setAvoidElements)}
                    />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Custom Prompt (Optional)"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!selectedApp || loading}
            >
              {loading ? "Generating Splash Pair..." : "Generate Splash Pair"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Generated Splash Screens ({records.length})
      </Typography>
      <Grid container spacing={2}>
        {records.map((record) => (
          <Grid item xs={12} md={6} key={record.splashId}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Light
                    </Typography>
                    <Box
                      component="img"
                      src={record.lightImageUrl}
                      alt="Light splash"
                      sx={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Dark
                    </Typography>
                    <Box
                      component="img"
                      src={record.darkImageUrl}
                      alt="Dark splash"
                      sx={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => openExportDialog(record)}
                >
                  Prepare Capacitor ZIP
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(record)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Prepare Capacitor Splash Assets</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generates `assets/splash.png` and `assets/splash-dark.png` at
            2732x2732 for `@capacitor/assets`.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">Horizontal Position</Typography>
            <Slider
              value={cropSettings.offsetX}
              min={-100}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              onChange={(_, value) =>
                setCropSettings((prev) => ({
                  ...prev,
                  offsetX: Number(value),
                }))
              }
            />
            <Typography variant="body2">Vertical Position</Typography>
            <Slider
              value={cropSettings.offsetY}
              min={-100}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              onChange={(_, value) =>
                setCropSettings((prev) => ({
                  ...prev,
                  offsetY: Number(value),
                }))
              }
            />
            <Typography variant="body2">Zoom</Typography>
            <Slider
              value={cropSettings.zoom}
              min={1}
              max={3}
              step={0.01}
              valueLabelDisplay="auto"
              onChange={(_, value) =>
                setCropSettings((prev) => ({
                  ...prev,
                  zoom: Number(value),
                }))
              }
            />
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleGenerateVariants}
              disabled={!exportTarget || processingVariants}
            >
              {processingVariants ? "Preparing..." : "Generate Assets"}
            </Button>
            {processingVariants && <CircularProgress size={24} />}
          </Stack>

          <Grid container spacing={2}>
            {variants.map((variant) => (
              <Grid item xs={12} md={6} key={variant.fileName}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {variant.name}
                    </Typography>
                    <Box
                      component="img"
                      src={variant.dataUrl}
                      alt={variant.name}
                      sx={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                        borderRadius: 2,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1 }}
                    >
                      {variant.fileName} ({variant.width}x{variant.height})
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadZip}
            disabled={variants.length === 0}
          >
            Download ZIP
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
